import {
  TrustLevel,
  FamiliarityLevel,
  TrustAuditEntry,
  RiskStoryEntity,
  RiskStoryEntityContent,
  RawRiskStoryItem,
  RiskStoryRef,
  RiskStorySummary,
} from "./risk-story.types";

// Lower index = safer. Used to pick "current" (safest) vs "event-time" (riskiest).
const TRUST_RANK: TrustLevel[] = ["trusted", "unmanaged", "untrusted"];

export const normalizeTrustLevel = (raw?: string | null): TrustLevel => {
  const v = (raw ?? "").toString().trim().toLowerCase();
  if (v === "trusted" || v === "true") return "trusted";
  if (v === "untrusted" || v === "false") return "untrusted";
  return "unmanaged";
};

export const normalizeFamiliarity = (
  raw?: string | null
): FamiliarityLevel | undefined => {
  const v = (raw ?? "").toString().trim().toLowerCase();
  return v ? v : undefined;
};

// Seeded with "trusted" (the safest value) so any real element in the array
// will beat the seed and become the new maximum. Returns the seed "trusted"
// when given an empty array — callers must pass non-empty arrays
// (mapRiskStoryResponse always does, via extractItems).
export const riskiestTrust = (levels: TrustLevel[]): TrustLevel =>
  levels.reduce<TrustLevel>(
    (acc, l) => (TRUST_RANK.indexOf(l) > TRUST_RANK.indexOf(acc) ? l : acc),
    "trusted"
  );

// Seeded with "untrusted" (the riskiest value) so any real element in the
// array will beat the seed and become the new minimum. Returns the seed
// "untrusted" when given an empty array — callers must pass non-empty arrays
// (mapRiskStoryResponse always does, via extractItems).
export const safestTrust = (levels: TrustLevel[]): TrustLevel =>
  levels.reduce<TrustLevel>(
    (acc, l) => (TRUST_RANK.indexOf(l) < TRUST_RANK.indexOf(acc) ? l : acc),
    "untrusted"
  );

const extractItems = (raw: any): RawRiskStoryItem[] | null => {
  // Reject only explicit failures; tolerate responses with or without an `ok`
  // wrapper. Narrow this fan-out once the live event-risk-stories shape is confirmed.
  if (!raw || raw.ok === false) return null;
  const items = Array.isArray(raw)
    ? raw
    : raw?.value?.items ??
      raw?.value?.stories ??
      raw?.value ??
      raw?.items ??
      raw?.stories ??
      raw?.data ??
      null;
  if (!Array.isArray(items) || items.length === 0) return null;
  return items;
};

const dedupeContent = (
  rows: RiskStoryEntityContent[]
): RiskStoryEntityContent[] => {
  const seen = new Set<string>();
  const out: RiskStoryEntityContent[] = [];
  rows.forEach((r) => {
    const key = `${r.contentId ?? ""}|${r.contentName ?? ""}`;
    if (key === "|" || seen.has(key)) return;
    seen.add(key);
    out.push({ contentId: r.contentId, contentName: r.contentName });
  });
  return out;
};

// Source of truth for entities. Returns null when the event is unsupported
// (no risk-story items, or no at-risk entities in their context).
//
// The live event-risk-stories response nests entities under each item's
// `context`: `exposedToRefs` are the at-risk recipients we surface. Trust is not
// in this payload — a risk story existing means these entities were *untrusted*
// for this content at event time, so eventTimeTrust is "untrusted". Current
// trust/familiarity + audit log are filled in later by enrichEntities().
export const mapRiskStoryResponse = (
  raw: any
): RiskStoryEntity[] | null => {
  const items = extractItems(raw);
  if (!items) return null;

  const byEntity = new Map<
    string,
    { ref: RiskStoryRef; virtualLabelId?: string; content: RiskStoryEntityContent[] }
  >();

  items.forEach((it) => {
    const ctx = it.context ?? {};
    const content: RiskStoryEntityContent[] = ctx.contentRef
      ? [{ contentId: ctx.contentRef.id, contentName: ctx.contentRef.name }]
      : [];
    // Upload-event resilience (ADO-24689): upload / self-exposure events can come
    // back with an empty exposedToRefs even though the narrative says the
    // exposed-to entities are untrusted (the backend bug to fix in ADO-24688).
    // When that happens, the actor (exposedByRef) is the relevant entity, so we
    // fall back to it rather than dropping the whole event to the legacy UI.
    const exposedTo = Array.isArray(ctx.exposedToRefs) ? ctx.exposedToRefs : [];
    const refs =
      exposedTo.length > 0
        ? exposedTo
        : ctx.exposedByRef
        ? [ctx.exposedByRef]
        : [];
    refs.forEach((ref) => {
      const id = (ref?.id ?? "").trim();
      if (!id) return;
      if (!byEntity.has(id)) {
        byEntity.set(id, { ref, virtualLabelId: it.virtualLabelId, content: [] });
      }
      byEntity.get(id)!.content.push(...content);
    });
  });

  if (byEntity.size === 0) return null;

  const entities: RiskStoryEntity[] = [];
  byEntity.forEach(({ ref, virtualLabelId, content }, entityId) => {
    entities.push({
      entityId,
      entityName: ref.name ?? entityId,
      entityType: ref.type ?? "person",
      virtualLabelId,
      eventTimeTrust: "untrusted",
      currentTrust: "untrusted",
      eventTimeFamiliarity: undefined,
      currentFamiliarity: undefined,
      hasTrustChange: false,
      content: dedupeContent(content),
      auditLog: [],
    });
  });

  return entities;
};

// Event-level summary from the risk story, so the modal can render the risk
// content (severity, narrative, files, date) straight from the Risk Story API.
export const mapRiskStorySummary = (raw: any): RiskStorySummary => {
  const items = extractItems(raw) || [];
  let severity = 0;
  let date: string | undefined;
  const narratives: string[] = [];
  const contentRows: RiskStoryEntityContent[] = [];
  items.forEach((it) => {
    if (typeof it.severity === "number" && it.severity > severity) {
      severity = it.severity;
    }
    if (typeof it.txt === "string" && it.txt.trim()) narratives.push(it.txt.trim());
    const c = it.context && it.context.contentRef;
    if (c) contentRows.push({ contentId: c.id, contentName: c.name });
    if (it.addedAt && (!date || it.addedAt > date)) date = it.addedAt;
  });
  return { severity, narratives, content: dedupeContent(contentRows), date };
};

export interface AggregationEnrichment {
  currentTrust?: TrustLevel;
  currentFamiliarity?: FamiliarityLevel;
  auditLog: TrustAuditEntry[];
}

// Maps the effective-trust-familiarity-aggregations response (confirmed live):
//   { ok, value: { items: [ { userSetLabelId, level, userSetLabelLogs, ... } ] } }
// - current trust = userSetLabelId
// - familiarity    = level
// - audit log      = userSetLabelLogs: [{ id: <new trust>, updatedBy, updatedAt }]
//   ordered newest-first, so each entry's "from" is the next (older) entry's id.
// Fallbacks keep older field names working if a different endpoint variant is hit.
export const mapAggregationResponse = (raw: any): AggregationEnrichment => {
  const agg =
    raw?.value?.items?.[0] ??
    raw?.value?.familiarityAggregations?.[0] ??
    raw?.value?.[0] ??
    raw?.value ??
    null;
  if (!agg) {
    return { currentTrust: undefined, currentFamiliarity: undefined, auditLog: [] };
  }
  const rawLog: any[] =
    agg.userSetLabelLogs ??
    agg.trustAuditLog ??
    agg.auditLog ??
    agg.trustHistory ??
    [];
  const logs = Array.isArray(rawLog) ? rawLog : [];
  const auditLog: TrustAuditEntry[] = logs.map((e, i) => {
    // userSetLabelLogs entry: { id: <new trust>, updatedBy, updatedAt }.
    // The previous (older, next-in-array) entry's id is the "from" value.
    const toRaw = e.id ?? e.toTrustLevel ?? e.to;
    const fromRaw = e.fromTrustLevel ?? e.from ?? (logs[i + 1] && logs[i + 1].id);
    return {
      changedBy: e.updatedBy ?? e.changedBy ?? e.user ?? e.actor,
      changedAt:
        e.updatedAt ?? e.changedAt ?? e.timestamp ?? e.date ?? e.levelChangedAt,
      from: fromRaw ? normalizeTrustLevel(fromRaw) : undefined,
      to: toRaw ? normalizeTrustLevel(toRaw) : undefined,
    };
  });
  // userSetLabelId is the user-set trust state, level is familiarity. Accept
  // trustLevel/familiarityLevel too in case a different endpoint variant is hit.
  const rawTrust = agg.userSetLabelId ?? agg.trustLevel;
  const rawFamiliarity = agg.level ?? agg.familiarityLevel;
  return {
    currentTrust: rawTrust ? normalizeTrustLevel(rawTrust) : undefined,
    currentFamiliarity: normalizeFamiliarity(rawFamiliarity),
    auditLog,
  };
};
